import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig, defaultLegendConfig, defaultScaleTitleConfig } from '../../utils/chartConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const INFO = '#599BE3';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};


const PERCENTILE_LABELS = ['10th', '25th', '50th', '75th', '90th'];

const BalanceFanChart = React.forwardRef(({ percentileData, years, percentiles, age1 }, ref) => {
  const labels = years.map((y) => `${y}`);

  const data = {
    labels,
    datasets: [
      {
        label: '90th Percentile',
        data: percentileData[4],
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        pointRadius: 0,
        fill: false,
        order: 5,
      },
      {
        label: '75th\u201390th',
        data: percentileData[3],
        borderColor: 'transparent',
        backgroundColor: 'rgba(89, 155, 227, 0.19)',
        pointRadius: 0,
        fill: '-1',
        order: 4,
      },
      {
        label: '50th\u201375th',
        data: percentileData[2],
        borderColor: INFO,
        borderWidth: 2,
        backgroundColor: 'rgba(89, 155, 227, 0.31)',
        pointRadius: 0,
        fill: '-1',
        order: 3,
      },
      {
        label: '25th\u201350th',
        data: percentileData[1],
        borderColor: 'transparent',
        backgroundColor: 'rgba(89, 155, 227, 0.31)',
        pointRadius: 0,
        fill: '-1',
        order: 2,
      },
      {
        label: '10th\u201325th',
        data: percentileData[0],
        borderColor: 'transparent',
        backgroundColor: 'rgba(89, 155, 227, 0.19)',
        pointRadius: 0,
        fill: '-1',
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Portfolio Balance Over Time - Range of Outcomes',
        font: { size: 14 },
      },
      legend: {
        ...defaultLegendConfig,
        labels: {
          ...defaultLegendConfig.labels,
          filter: (item) => item.text !== '90th Percentile',
        },
      },
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            const year = years[idx];
            const age = age1 != null ? age1 + year : null;
            return age != null ? `Age ${age}` : `Year ${year}`;
          },
          label: () => null,
          afterTitle: (items) => {
            const idx = items[0]?.dataIndex;
            const lines = ['\nBalance at this age:'];
            percentileData.forEach((data, i) => {
              if (data[idx] !== undefined) {
                lines.push(`${PERCENTILE_LABELS[i]} percentile: ${formatCurrency(Math.round(data[idx]))}`);
              }
            });
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Age',
        },
        ticks: {
          callback: function (value, index) {
            const year = years[index];
            if (age1 != null) {
              return `${age1 + year}`;
            }
            return `${year}`;
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Portfolio Balance ($)',
        },
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Line ref={ref} data={data} options={options} />
    </div>
  );
});

BalanceFanChart.displayName = 'BalanceFanChart';

export default BalanceFanChart;
